import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, Input, Modal } from 'antd';
import type { InputRef } from 'antd';
import { ArrowRightOutlined, SearchOutlined } from '@ant-design/icons';
import { searchManagementEntries } from './navigation';

export default function AdminCommandMenu({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<InputRef>(null);
  const resultList = useRef<HTMLDivElement>(null);
  const results = searchManagementEntries(query).slice(0, 10);
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);
  useEffect(() => {
    resultList.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);
  const choose = (path: string) => {
    onClose();
    navigate(path);
  };
  return (
    <Modal
      title="查找管理功能"
      open={open}
      onCancel={onClose}
      footer={null}
      width={650}
      afterOpenChange={(visible) => {
        if (visible) input.current?.focus();
      }}
    >
      <Input
        ref={input}
        size="large"
        prefix={<SearchOutlined />}
        placeholder="搜索食品、订单、提醒、AI、用户…"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
        }}
        role="combobox"
        aria-label="搜索管理功能"
        aria-autocomplete="list"
        aria-expanded
        aria-controls="admin-command-results"
        aria-activedescendant={results[active] ? `admin-command-${active}` : undefined}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((value) => Math.min(value + 1, results.length - 1));
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((value) => Math.max(value - 1, 0));
          }
          if (event.key === 'Enter' && results[active]) {
            event.preventDefault();
            choose(results[active].key);
          }
        }}
      />
      <div
        ref={resultList}
        className="admin-command-results"
        id="admin-command-results"
        role="listbox"
        aria-label="管理功能搜索结果"
      >
        {results.map((item, index) => (
          <button
            type="button"
            role="option"
            aria-selected={active === index}
            id={`admin-command-${index}`}
            className={`admin-command-option${active === index ? ' is-active' : ''}`}
            key={item.key}
            onClick={() => choose(item.key)}
            onMouseEnter={() => setActive(index)}
          >
            <span className="admin-command-icon">{item.icon}</span>
            <span>
              <strong>{item.label}</strong>
              <small>
                {item.group} · {item.description}
              </small>
            </span>
            <ArrowRightOutlined />
          </button>
        ))}
        {!results.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="没有找到相关功能，试试其他关键词"
          />
        )}
      </div>
      <div className="admin-command-help">↑ ↓ 选择 · Enter 打开 · Esc 关闭</div>
    </Modal>
  );
}
